import { createContext, useContext, useEffect, useState } from 'react';
import { AppState, Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import axios, { AxiosRequestConfig } from 'axios';
import { useDispatch } from 'react-redux';
import { setAuthCallbackState } from '@/application/actions/landing';
import { routePaths } from '../constants';
import i18nMain from '@/config/i18n';
import { isCustomDomain } from '@/util';

const domain = import.meta.env.REACT_APP_AUTH0_DOMAIN;
const defaultClientId = import.meta.env.REACT_APP_AUTH0_CLIENT_ID;
const SESSION_STORAGE_KEY_AUTH0_CONFIG = 'lt-auth0-config';
export const DEFAULT_AUTH_SCOPES =
  'openid profile email offline_access https://lemontaps.com/claim/';

export default function LtAuth0Provider(props: { children: React.ReactNode }) {
  const dispatch = useDispatch();

  const [clientId, setClientId] = useState<string>(initClientId());
  useEffect(() => {
    const fetchClientId = async () => {
      let clientId: string;
      try {
        const fetchedConfig = await axios.get<{ clientId?: string }>('/auth0-config.json');
        clientId = fetchedConfig.data.clientId || defaultClientId;
      } catch {
        clientId = defaultClientId;
      }
      sessionStorage.setItem(SESSION_STORAGE_KEY_AUTH0_CONFIG, JSON.stringify({ clientId }));
      setClientId(clientId);
    };
    if (!clientId) fetchClientId();
  }, [clientId]);

  if (!clientId) {
    return <>{props.children}</>;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      onRedirectCallback={(state, user) => {
        if (
          state &&
          state.signup &&
          (state.ltSignupCode ||
            state.signupToTier ||
            state.ltReferralKey ||
            state.ltSignupToTheme ||
            state.utmParams)
        ) {
          axios.interceptors.request.use(afterSignupInterceptor(state), error => {
            return Promise.reject(error);
          });
        }
        dispatch(setAuthCallbackState(state || {}));

        if (user?.['https://lemontaps.com/claim/analytics/is_signup']) {
          // delay this until gtm is available
          const interval = setInterval(() => {
            // wait until user has interacted with the cookie banner
            const userAlreadyInteracted =
              'localStorage' in window
                ? window.localStorage.getItem('uc_user_interaction') === 'true'
                : false; // some old webviews don't support localStorage
            if (userAlreadyInteracted && window.dataLayer && Array.isArray(window.dataLayer)) {
              setTimeout(() => {
                try {
                  window.dataLayer.push({ event: 'signup' }); // event is configured in GTM
                } catch (error) {
                  console.log('cb', 'error pushing signup event to dataLayer', error);
                }
              }, 500); // delay to ensure gtm is configured and possible click ids are picked up
              clearInterval(interval);
            }
          }, 500);
          setTimeout(() => clearInterval(interval), 600000); // after 10 minutes
        }
      }}
      authorizationParams={{
        redirect_uri: window.location.origin + routePaths.HOME,
        audience: import.meta.env.REACT_APP_AUTH0_API_AUDIENCE,
        scope: DEFAULT_AUTH_SCOPES,
      }}
      useRefreshTokens={isCustomDomain()}
      cacheLocation='memory' // enable this for security
      // cacheLocation={isCustomDomain() ? 'localstorage' : 'memory'} // only enable after eng-1323 is fixed
    >
      <Auth0ClientIdContext.Provider value={{ clientId }}>
        {props.children}
      </Auth0ClientIdContext.Provider>
    </Auth0Provider>
  );
}

const initClientId = () => {
  if (!isCustomDomain) return defaultClientId;
  try {
    const storedConfig = sessionStorage.getItem(SESSION_STORAGE_KEY_AUTH0_CONFIG);
    if (storedConfig) {
      const parsedConfig = JSON.parse(storedConfig);
      if (typeof parsedConfig.clientId === 'string') return parsedConfig.clientId;
    }
  } catch {
    //
  }
  return null;
};

let askedForUnitName = false;
const afterSignupInterceptor = (state: AppState) => (config: AxiosRequestConfig) => {
  let newUnitName = state.ltUnitName || '';
  if (state.ltSignupToUnit && !askedForUnitName) {
    if (state.ltUnitName) {
      newUnitName = state.ltUnitName;
    } else {
      while (!newUnitName) {
        newUnitName = prompt(
          i18nMain.language?.startsWith('de')
            ? 'Gib hier Deinen Firmennamen ein:'
            : 'Please enter your company name here:',
        );
      }
    }
    askedForUnitName = true;
  }

  if (state.ltSignupToTheme && sessionStorage.getItem('lt-signup2theme-reload') !== 'reloaded') {
    sessionStorage.setItem('lt-signup2theme-reload', 'true');
  }
  if (config.params) {
    config.params = {
      ...config.params,
      'lt-signup-code': state.ltSignupCode,
      'lt-signup-to-tier': state.signupToTier?.tier,
      'lt-signup-to-trial': state.signupToTier?.trial,
      'lt-plan': state.signupToTier?.plan,
      'lt-referral-key': state.ltReferralKey,
      'lt-signup-to-theme': state.ltSignupToTheme ? state.ltThemeName : undefined,
      'lt-unit-name': state.ltSignupToUnit ? newUnitName : undefined,
      'lt-token': state.ltToken ? state.ltToken : undefined,
      'utm-params': state.utmParams ? state.utmParams : undefined,
    };
  } else {
    config.params = {
      'lt-signup-code': state.ltSignupCode,
      'lt-signup-to-tier': state.signupToTier?.tier,
      'lt-signup-to-trial': state.signupToTier?.trial,
      'lt-plan': state.signupToTier?.plan,
      'lt-referral-key': state.ltReferralKey,
      'lt-signup-to-theme': state.ltSignupToTheme ? state.ltThemeName : undefined,
      'lt-unit-name': state.ltSignupToUnit ? newUnitName : undefined,
      'lt-token': state.ltToken ? state.ltToken : undefined,
      'utm-params': state.utmParams ? state.utmParams : undefined,
    };
  }
  return config;
};

const Auth0ClientIdContext = createContext<{
  clientId: string;
}>({
  clientId: '',
});

// TODO add esline rule to disallow using the logout function from auth0 directly
export const useLogout = (config: { generic?: boolean } = { generic: false }) => {
  const { logout } = useAuth0();
  const context = useContext(Auth0ClientIdContext);
  if (!context || !logout) {
    return () => {};
  }
  if (config.generic) return () => logout();
  else
    return (conf: { returnTo?: string } = {}) =>
      logout({
        clientId: context.clientId,
        logoutParams: {
          returnTo: conf.returnTo || window.location.origin + '/login',
        },
      });
};
